import classNames from 'classnames';
import { flowRight } from 'lodash';
import {
  isLayoutTextOnImage,
  type Layout,
  type MixedLayoutTypeName,
} from '@wix/communities-blog-client-common';
import { connect } from '../components/runtime-context';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type useLayoutColorClasses from '../hooks/use-layout-color-classes';
import { getUseMobileLayoutSettings } from '../selectors/app-settings-selectors';
import { getFeedColorClassName } from '../services/layout-config';
import { type AppState } from '../types';
import withHocName from './with-hoc-name';
import withIsFeedDesignEnabled, {
  type WithIsFeedDesignEnabled,
} from './with-is-feed-design-enabled';
import withLayoutProps, {
  type WithLayoutOwnProps,
  type WithLayoutProps,
} from './with-layout-props';

import styles from '../styles/post-list-item.scss';

export type WithLayoutColorClasses = {
  iconColorClassName: string;
  textColorClassName: string;
  separatorBackgroundColorClassName: string;
};

type LayoutColorClassesOwnProps = WithIsFeedDesignEnabled &
  WithLayoutOwnProps &
  WithLayoutProps & {
    iconColorClassName?: string;
    textColorClassName?: string;
    separatorBackgroundColorClassName?: string;
  };

const layoutColorClasses = (
  getPostClassName: (...classNames: string[]) => string[],
  getFeedColorClassNameFn: (layoutName: MixedLayoutTypeName, colorType: string) => string,
  layoutName: MixedLayoutTypeName,
) => ({
  iconColorClassName: classNames(
    getPostClassName(getFeedColorClassNameFn(layoutName, 'description-fill')),
  ),
  textColorClassName: classNames(
    getPostClassName(
      getFeedColorClassNameFn(layoutName, 'description-color'),
      getFeedColorClassNameFn(layoutName, 'description-fill'),
      'description-font',
    ),
  ),
  separatorBackgroundColorClassName: classNames(
    getPostClassName(getFeedColorClassNameFn(layoutName, 'description-background-color')),
  ),
});

const layoutColorClassesBeforeMigration = (
  layoutType: Layout | undefined,
  iconColorClassName: string | undefined,
  textColorClassName: string | undefined,
  separatorBackgroundColorClassName: string | undefined,
) =>
  isLayoutTextOnImage(layoutType)
    ? {
        iconColorClassName: styles.textOnImageIcons,
        textColorClassName: styles.textOnImageTextColor,
        separatorBackgroundColorClassName: styles.textOnImageSeperatorBackgroundColor,
      }
    : {
        iconColorClassName: iconColorClassName || 'blog-icon-fill',
        textColorClassName: textColorClassName || 'blog-text-color',
        separatorBackgroundColorClassName:
          separatorBackgroundColorClassName || 'blog-separator-background-color',
      };

const mapRuntimeToProps = (
  state: AppState,
  {
    applyFeedDesign,
    layoutType,
    layoutName,
    mobileLayoutName,
    getPostClassName,
    iconColorClassName,
    textColorClassName,
    separatorBackgroundColorClassName,
  }: LayoutColorClassesOwnProps,
) => {
  const currentLayoutName = getUseMobileLayoutSettings(state) ? mobileLayoutName : layoutName;
  return applyFeedDesign
    ? layoutColorClasses(getPostClassName, getFeedColorClassName, currentLayoutName)
    : layoutColorClassesBeforeMigration(
        layoutType,
        iconColorClassName,
        textColorClassName,
        separatorBackgroundColorClassName,
      );
};

/** @deprecated use {@link useLayoutColorClasses} hook instead */
const withLayoutColorClasses = flowRight(
  withLayoutProps({ fallbackEnabled: false }),
  withIsFeedDesignEnabled,
  connect(mapRuntimeToProps),
  withHocName('WithLayoutColorClasses'),
);

export default withLayoutColorClasses;
